import { BaseVariableProps, SearchByEmail } from "../EmailBuilderTypes";
import VariableCollapse, { VariablePicker } from "./VariableCollapse";
import { Button, Input } from "reactstrap";
import AppEmptyState from "../../../../elements/AppEmptyState";
import ModulePicker from "../../../Modules/ModulePicker";
import useModule from "../../../../hooks/useModule";
import { SelectPicker } from "rsuite";
import FieldUtils from "../../../../utils/FieldUtils";
import IconButton from "../../../../elements/IconButton";

const SearchByEmailBuilder = ({
  variable,
  setVariables,
  index,
  customVariables,
  expandAll,
}: BaseVariableProps) => {
  const isSearchByEmail = variable.Type === "SearchByEmail";
  const { module } = useModule(isSearchByEmail ? variable.ModuleAPIName : "");

  if (variable.Type !== "SearchByEmail") return null;

  const hasVariables = Boolean(variable.Variables.length);

  const Builder = (
    <div className="pt-2 px-4 pb-4">
      <h4>Data Source</h4>
      <ModulePicker
        cleanable={false}
        value={variable.ModuleAPIName}
        onChange={(newModule: string | null) => {
          const newMod = newModule || "";
          updateVariable("ModuleAPIName", newMod);
        }}
      />
      <div className="d-flex align-items-center justify-content-between my-4">
        <h4 className="mb-0">Variables</h4>
        <Button
          size="sm"
          color="primary"
          onClick={addSearchByEmailVariable}
          disabled={!variable.ModuleAPIName}
        >
          Add Variable
        </Button>
      </div>
      {!hasVariables && (
        <AppEmptyState
          title="No variables added!"
          description="No Search By Email variables have been added."
          hideIcon
        />
      )}
      {hasVariables && (
        <div className="d-flex justify-content-between">
          <h4>Variable Name</h4>
          <h4 style={{ paddingRight: 210 }}>Field</h4>
        </div>
      )}
      {variable.Variables.map((variable, i) => {
        return (
          <div
            key={i}
            className="py-2 d-flex justify-content-between btn-oauth bg-white rounded my-2 px-2"
          >
            {customVariables ? (
              <VariablePicker
                vars={customVariables}
                variable={{ ...variable, Type: "Value" }}
                updateVariableName={(newName: string) =>
                  updateSearchByEmailVariable(i, "Variable", newName)
                }
              />
            ) : (
              <Input
                bsSize="sm"
                placeholder="Variable Name"
                style={{ minWidth: 200, width: "100%", maxWidth: 250 }}
                value={variable.Variable}
                onChange={(e) =>
                  updateSearchByEmailVariable(i, "Variable", e.target.value)
                }
              />
            )}
            <div className="d-flex align-items-center">
              <SelectPicker
                preventOverflow
                placeholder={"Select Field"}
                cleanable={false}
                style={{ minWidth: 200, width: "100%", maxWidth: 250 }}
                value={variable.FieldAPIName}
                data={
                  module?.Fields?.flatMap?.((f: any) => {
                    if (FieldUtils.fieldTypeIsSubitem(f?.FieldType)) {
                      return [];
                    }

                    return [
                      {
                        label: f?.Name,
                        value: f?.APIName,
                        type: f?.FieldType,
                      },
                    ];
                  }) ?? []
                }
                onSelect={(fieldAPIName, fieldObj) => {
                  const fieldType = fieldObj?.type;

                  if (!fieldAPIName || !fieldType) return;

                  updateSearchByEmailVariable(i, "FieldAPIName", fieldAPIName);
                }}
              />

              <IconButton
                icon="close"
                color="danger"
                onClick={() => removeSearchByEmailVariable(i)}
                tooltipText={"Remove variable"}
                className={"ml-2"}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <VariableCollapse
      variable={variable}
      setVariables={setVariables}
      index={index}
      hideInput="Open to see variable names"
      expandAll={expandAll}
    >
      {Builder}
    </VariableCollapse>
  );

  function updateVariable(key: keyof SearchByEmail, value: string) {
    setVariables((prev) => {
      return prev.map((variable, i) => {
        if (i !== index) return variable;

        return { ...variable, [key]: value };
      });
    });
  }

  function addSearchByEmailVariable() {
    setVariables((prev) => {
      return prev.map((variable, i) => {
        if (i !== index) return variable;
        if (variable.Type !== "SearchByEmail") return variable;

        return {
          ...variable,
          Variables: [
            ...variable.Variables,
            { Variable: "", FieldAPIName: "" },
          ],
        };
      });
    });
  }

  function removeSearchByEmailVariable(removeIndex: number) {
    setVariables((prev) => {
      return prev.map((variable, i) => {
        if (i !== index) return variable;
        if (variable.Type !== "SearchByEmail") return variable;

        return {
          ...variable,
          Variables: variable.Variables.filter((_, vI) => {
            return vI !== removeIndex;
          }),
        };
      });
    });
  }

  function updateSearchByEmailVariable(
    updateIndex: number,
    keyToUpdate: "Variable" | "FieldAPIName",
    newValue: string
  ) {
    setVariables((prev) => {
      return prev.map((variable, i) => {
        if (i !== index) return variable;
        if (variable.Type !== "SearchByEmail") return variable;

        return {
          ...variable,
          Variables: variable.Variables.map((nestedVariable, vI) => {
            if (vI !== updateIndex) return nestedVariable;

            return { ...nestedVariable, [keyToUpdate]: newValue };
          }),
        };
      });
    });
  }
};
export default SearchByEmailBuilder;
